/**
 * The asset card's host (`equipment-page-asset-card`): binds the card to this page's services — the
 * identify write for a row with no product (`useRegisterIdentifyBinding` + `useIdentifyGroup`, as
 * Floor Plan's machine card binds it), the table's writes (`useEquipmentRowWrites`), and the page's
 * removal steps — and puts *Remove From Register* in its footer.
 *
 * Mounted for the whole Rows view, with or without an open row: the identify binding's toasts (a
 * first identification's Undo, its Try again) are dismissed when it unmounts, so they must outlive
 * the card closing. With no row open it draws nothing.
 */
import React, { useMemo, useRef } from 'react';
import { useIdentifyGroup } from '@repo/ui';
import type { TwinAssetResponse } from '@repo/front-end-services';
import {
  registerRowName,
  type AssetIdentityState,
  type AssetPlanState,
} from '@repo/front-end-utils';
import type { FloorPlannerLayout } from '@repo/frontend-types';

import { useRegisterIdentifyBinding } from './EquipmentIdentify';
import {
  EquipmentAssetCard,
  type AssetCardFolds,
  type AssetCardIdentify,
  type EquipmentAssetCardHandle,
} from './EquipmentAssetCard';
import { EquipmentAssetRemove, type AssetRemoval } from './EquipmentAssetRemove';
import { useEquipmentRowWrites, type EquipmentWriteDeps } from './useEquipmentRowWrites';

export interface EquipmentAssetCardHostProps {
  /** The open row; null while the card is closed. */
  row: TwinAssetResponse | null;
  rows: readonly TwinAssetResponse[];
  layout: FloorPlannerLayout | null;
  state: AssetIdentityState | null;
  planKnown: boolean;
  livePlanStateOf?: (id: number) => AssetPlanState | undefined;
  twinId: number | null;
  baseUrl: string;
  getToken: () => Promise<string>;
  currentTwinId: () => number | null;
  reloadRegister: () => void;
  beginRegisterWrite: () => number | null;
  settleRegisterWrite: (
    token: number | null,
    outcome: { ok: boolean; reason?: string | null },
  ) => void;
  /** The table's write dependencies — the same the Rows view is given. */
  writeDeps: EquipmentWriteDeps;
  loadModelFiles: (options?: { refresh?: boolean }) => Promise<string[]>;
  /** A row with no product had its 3D model cleared: the plan's copy goes too. */
  onModelCleared?: (rowId: number) => void;
  removal: AssetRemoval;
  folds: AssetCardFolds;
  onFoldsChange: (folds: AssetCardFolds) => void;
  onClose: () => void;
  /** Removed: the page's notice, the card closes and focus moves on (R20). */
  onRemoved: (notice: string) => void;
  returnFocusTo?: { readonly current: HTMLElement | null };
  cardRef?: React.Ref<EquipmentAssetCardHandle>;
}

export const EquipmentAssetCardHost: React.FC<EquipmentAssetCardHostProps> = ({
  row,
  rows,
  layout,
  state,
  planKnown,
  livePlanStateOf,
  twinId,
  baseUrl,
  getToken,
  currentTwinId,
  reloadRegister,
  beginRegisterWrite,
  settleRegisterWrite,
  writeDeps,
  loadModelFiles,
  onModelCleared,
  removal,
  folds,
  onFoldsChange,
  onClose,
  onRemoved,
  returnFocusTo,
  cardRef,
}) => {
  // The binding's map of groups a write has just landed on. The card writes one row and moves on to
  // nothing, so nothing reads it; the binding requires one.
  const settling = useRef(new Map<string, readonly number[]>());
  const { api, fetchers } = useRegisterIdentifyBinding({
    twinId,
    baseUrl,
    getToken,
    currentTwinId,
    reloadRegister,
    beginRegisterWrite,
    settleRegisterWrite,
    settling,
  });
  const { busy, identify } = useIdentifyGroup(api);
  const writes = useEquipmentRowWrites(writeDeps);

  const identifyBinding = useMemo<AssetCardIdentify>(
    () => ({
      fetchers,
      busy,
      logContext: twinId !== null ? { twinId } : undefined,
      identifyRow: (rowId, referenceId, label) =>
        identify({ label }, referenceId, [rowId], failed => failed),
    }),
    [fetchers, busy, identify, twinId],
  );

  if (!row) return null;
  return (
    <EquipmentAssetCard
      ref={cardRef}
      row={row}
      rows={rows}
      layout={layout}
      state={state}
      planKnown={planKnown}
      livePlanStateOf={livePlanStateOf}
      writes={writes}
      identify={identifyBinding}
      loadModelFiles={loadModelFiles}
      onModelCleared={onModelCleared}
      folds={folds}
      onFoldsChange={onFoldsChange}
      onClose={onClose}
      returnFocusTo={returnFocusTo}
      removeControl={refuse => (
        <EquipmentAssetRemove
          rowId={row.id}
          name={registerRowName(row)}
          removal={removal}
          onRefused={refuse}
          onRemoved={onRemoved}
        />
      )}
    />
  );
};
